import { useEffect, useRef, useState } from "react";

export default function FlashFocus({
  word,
  transliteration,
  audioUrl,
  duration = 15000,
  backText,
  backgroundUrl,
  repeatInterval = 700,
  speechGapMs = 120,
  usageExample,
  onComplete
}) {
  const [active, setActive] = useState(true);
  const [progress, setProgress] = useState(0);

  const activeRef = useRef(false);
  const overlayRef = useRef(null);
  const bgIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const speakTimeoutRef = useRef(null);
  const repeatIntervalRef = useRef(null);
  const flyIntervalRef = useRef(null);
  const baseAudioRef = useRef(null);
  const audioLayersRef = useRef([]);

  const softColor = () =>
    `hsl(${Math.random() * 360}, 35%, 12%)`;

  const speak = (text, url) => {
    if (url) {
      if (!baseAudioRef.current || baseAudioRef.current.src !== url) {
        baseAudioRef.current = new Audio(url);
        baseAudioRef.current.preload = "auto";
        baseAudioRef.current.load();
      }

      const playLayer = () => {
        if (!activeRef.current) return;
        const audio = baseAudioRef.current.cloneNode(true);
        audio.currentTime = 0;
        audioLayersRef.current.push(audio);
        audio.onended = () => {
          audioLayersRef.current = audioLayersRef.current.filter((a) => a !== audio);
        };
        audio.play().catch(() => {});
      };

      playLayer();

      if (repeatInterval > 0) {
        clearInterval(repeatIntervalRef.current);
        repeatIntervalRef.current = setInterval(playLayer, repeatInterval);
      }
      return;
    }

    if (!window.speechSynthesis) return;

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "en-US";
    utter.rate = 0.95;

    utter.onend = () => {
      if (activeRef.current) {
        speakTimeoutRef.current = setTimeout(() => {
          speak(text, url);
        }, speechGapMs);
      }
    };

    window.speechSynthesis.speak(utter);
  };

  const requestStop = () => {
    stop();
  };

  const createFlyingWord = () => {
    const el = document.createElement("div");
    el.innerText = word;
    el.style.position = "absolute";
    el.style.fontWeight = "bold";
    el.style.color = "white";
    el.style.opacity = "0.8";
    el.style.pointerEvents = "none";

    const size = 20 + Math.random() * 40;
    el.style.fontSize = `${size}px`;

    const directions = [
      { x: "-30vw", y: "0" },
      { x: "30vw", y: "0" },
      { x: "0", y: "-30vh" },
      { x: "0", y: "30vh" },
      { x: "-30vw", y: "-30vh" },
      { x: "30vw", y: "30vh" },
    ];

    const dir = directions[Math.floor(Math.random() * directions.length)];

    el.style.setProperty("--startX", dir.x);
    el.style.setProperty("--startY", dir.y);

    el.style.animation = "flyIn 2s ease-out forwards";

    overlayRef.current.appendChild(el);

    setTimeout(() => {
      el.remove();
    }, 2000);
  };

  const start = () => {
    setActive(true);
  };

  const stop = ({ resetUi = true } = {}) => {
    activeRef.current = false;
    if (resetUi) {
      setActive(false);
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    audioLayersRef.current.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
    });
    audioLayersRef.current = [];
    if (baseAudioRef.current) {
      baseAudioRef.current.pause();
      baseAudioRef.current.currentTime = 0;
    }

    clearInterval(bgIntervalRef.current);
    clearInterval(progressIntervalRef.current);
    clearInterval(repeatIntervalRef.current);
    clearInterval(flyIntervalRef.current);
    clearTimeout(speakTimeoutRef.current);

    if (resetUi) {
      setProgress(0);
    }
  };

  useEffect(() => {
    activeRef.current = active;
    if (!active) return;
    // background đổi nhẹ
    bgIntervalRef.current = setInterval(() => {
      document.body.style.background = softColor();
    }, 2500);

    // progress
    let elapsed = 0;
    progressIntervalRef.current = setInterval(() => {
      elapsed += 100;
      setProgress(Math.min((elapsed / duration) * 100, 100));
    }, 100);

    // chữ bay liên tục
    flyIntervalRef.current = setInterval(() => {
      createFlyingWord();
    }, 400);

    // speak
    speak(word, audioUrl);

    const timer = setTimeout(() => {
      requestStop();
      if (typeof onComplete === "function") {
        onComplete();
      }
    }, duration);

    return () => {
      clearTimeout(timer);
      stop({ resetUi: false });
      document.body.style.background = "#111";
    };
  }, [active, audioUrl, duration, onComplete, repeatInterval, speechGapMs, word]);

  if (!active) {
    return (
      <button
        onClick={start}
        style={{
          padding: "12px 20px",
          background: "#111",
          color: "white",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        Focus: {word}
      </button>
    );
  }

  return (
  <div
    ref={overlayRef}
    style={{
      position: "fixed",
      inset: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      background: backgroundUrl
        ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url("${backgroundUrl}") center/cover no-repeat`
        : "#111",
      color: "white",
      overflow: "hidden",
      zIndex: 999,
    }}
  >
    {transliteration && (
      <div
        style={{
          position: "absolute",
          top: 100,
          left: "50%",
          transform: "translateX(-50%)",
          padding: "6px 12px",
          borderRadius: 999,
          background: "rgba(255,255,255,1)",
          fontSize: 16,
          fontWeight: 600,
          color: "#111010",
          textShadow: "0 2px 8px rgba(0,0,0,0.45)",
          zIndex: 4,
        }}
      >
        {transliteration}
      </div>
    )}
    

    {backText && (
      <div
        style={{
          position: "absolute",
          top: 150,
          left: "50%",
          transform: "translateX(-50%)",
          padding: "6px 12px",
          borderRadius: 999,
          background: "rgba(255,255,255,1)",
          fontSize: 16,
          fontWeight: 600,
          color: "#0e0d0d",
          textShadow: "0 2px 8px rgba(0,0,0,0.45)",
          zIndex: 4,
        }}
      >
        {backText}
      </div>
    )}

    {usageExample && (
      <div
        style={{
          position: "absolute",
          top: 200,
          left: "50%",
          transform: "translateX(-50%)",
          padding: "6px 12px",
          borderRadius: 999,
          background: "rgba(255,255,255,1)",
          fontSize: 16,
          fontWeight: 600,
          color: "#331cdb",
          textShadow: "0 2px 8px rgba(0,0,0,0.45)",
          zIndex: 4,
        }}
      >
        {usageExample}
      </div>
    )}

    <div
      style={{
        position: "absolute",
        bottom: 40,
        width: "60%",
        textAlign: "center",
      }}
    >
      <div
        style={{
          height: 12,
          background: "rgba(255,255,255,0.2)",
          borderRadius: 10,
          overflow: "hidden",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${progress}%`,
            background: "#00ff99",
            transition: "width 0.1s linear",
          }}
        />
      </div>
      <div>{Math.floor(progress)}%</div>
    </div>

    <style>{`
      @keyframes flyIn {
        from {
          transform: translate(var(--startX), var(--startY)) scale(0.4);
          opacity: 0;
        }
        to {
          transform: translate(0, 0) scale(1);
          opacity: 1;
        }
      }
    `}</style>
  </div>
);
}
